import { useEffect } from 'react';
import { useRouterState } from '@tanstack/react-router';
import { track } from '@repo/shared/client';

/**
 * Central page-view analytics. Mounted ONCE in `__root`'s RootComponent, which
 * wraps every route — so the `page_viewed` event fires on first mount and on
 * every subsequent navigation across EVERY product (and unauthed) surface,
 * with no per-route opt-in. This satisfies the ADHERENCE "every user-facing
 * surface emits page-view events" rule in one place.
 *
 * `pageName` is the stable leaf route id (e.g. `/_protected/home`); `path` is
 * the live `location.pathname`. The effect keys on both, so it fires once per
 * distinct navigation and never double-fires for the same path.
 */
export function PageViewTracker(): null {
  // A SINGLE selector reads pathname + leaf route id from one committed router
  // state and joins them into one primitive key. Reading both in one
  // subscription avoids tearing between two `useRouterState` hooks (which can
  // momentarily disagree mid-navigation and fire an inconsistent event); the
  // primitive key also dodges re-render churn from a fresh object each render.
  const key = useRouterState({
    select: (state) => {
      // Read BOTH fields from the same leaf match so they're always mutually
      // consistent. During a navigation TanStack commits the new
      // `location.pathname` one state before `matches` resolve, so deriving
      // `path` from `location.pathname` (instead of the leaf) would fire an
      // extra event carrying the new path with the previous route's name. The
      // leaf's own `pathname` only advances once the match settles → exactly
      // one event per navigation, never an inconsistent (name, path) pair.
      const leaf = state.matches[state.matches.length - 1];
      const pageName = leaf?.routeId ?? state.location.pathname;
      const path = leaf?.pathname ?? state.location.pathname;
      return `${pageName}\n${path}`;
    },
  });

  useEffect(() => {
    const [pageName, path] = key.split('\n');
    track('page_viewed', { pageName: pageName ?? '', path: path ?? '' });
  }, [key]);

  return null;
}
